import { PageSize } from '@/constant';
import {
	TForeignSystemUnitItem,
	TGetListForeignSystemUnitParams,
	TPayloadUpdateSuccess,
} from '@/services';
import { mergeItemUpdated, mergeListItemUpdated } from '@/utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
	IContractedKoreanBusinessDetailStore,
	IPayloadGetContractedKoreanBusinessListRequest,
	IPayloadGetContractedKoreanBusinessListSuccess,
	RootState,
} from '..';
import {
	formatForeignSystemUnitDetail,
	formatForeignSystemUnitList,
} from './unit.helper';
import {
	ICreateForeignUnitRequest,
	IForeignUnitDetailStore,
	IPayloadDeleteForeignUnitRequest,
	IPayloadGetForeignUnitDetailRequest,
	IPayloadGetForeignUnitDetailSuccess,
	IPayloadGetForeignUnitListRequest,
	IPayloadGetForeignUnitListSuccess,
	TForeignSystemUnitHistoryUpdateDetailStore,
	TPayloadGetForeignSystemHistoryUnitListSuccess,
	TPayloadGetHistoryUpdateForeignUnitListRequest,
} from './unit.type';

const InitParams: TGetListForeignSystemUnitParams = {
	page: 1,
	pageSize: PageSize.S,
	searchTypes: [],
};

const initialState = {
	fetching: false,
	unitList: [] as IForeignUnitDetailStore[],
	total: 0,
	params: InitParams,
	unitDetail: {} as IForeignUnitDetailStore,
	historyUpdateUnitList: [] as TForeignSystemUnitHistoryUpdateDetailStore[],
	historyParams: {
		page: 1,
		pageSize: PageSize.XXS,
		field: '',
		total: 0,
	},
	contractedKoreanBusiness: {
		fetching: false,
		data: [] as IContractedKoreanBusinessDetailStore[],
		total: 0,
		params: {
			page: 1,
			pageSize: PageSize.S,
		},
	},
};

const ForeignSystemUnitSlice = createSlice({
	initialState,
	name: 'foreignSystemUnit',
	reducers: {
		/**---- GET COMPANY_MANAGEMENT LIST START ---- */
		getSystemUnitListRequest: (
			state,
			action: PayloadAction<IPayloadGetForeignUnitListRequest>
		) => {
			return {
				...state,
				fetching: true,
				params: action?.payload,
			};
		},
		getSystemUnitListSuccess: (
			state,
			action: PayloadAction<IPayloadGetForeignUnitListSuccess>
		) => {
			const { unitList, total } = action.payload;
			state.fetching = false;
			state.unitList = unitList;
			state.total = total;
		},
		getSystemUnitListFailure: (state) => {
			state.fetching = false;
			state.total = 0;
			state.unitList = [];
		},
		/**---- GET COMPANY_MANAGEMENT LIST END ---- */

		/**---- CREATE COMPANY_MANAGEMENT START ---- */
		createSystemUnitRequest: (
			state,
			action: PayloadAction<ICreateForeignUnitRequest>
		) => {
			state.fetching = true;
		},
		createSystemUnitSuccess: (state) => {
			state.fetching = false;
		},
		createSystemUnitFailure: (state) => {
			state.fetching = false;
		},
		/**---- CREATE COMPANY_MANAGEMENT END ---- */

		/**---- GET COMPANY_MANAGEMENT DETAIL START ---- */
		getSystemUnitDetailRequest: (
			state,
			action: PayloadAction<IPayloadGetForeignUnitDetailRequest>
		) => {
			state.fetching = true;
		},
		getSystemUnitDetailSuccess: (
			state,
			action: PayloadAction<IPayloadGetForeignUnitDetailSuccess>
		) => {
			const { unitDetail } = action.payload;
			state.fetching = false;
			state.unitDetail = unitDetail;
		},
		getSystemUnitDetailFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET COMPANY_MANAGEMENT DETAIL END ---- */

		/**---- DELETE COMPANY_MANAGEMENT START ---- */
		deleteSystemUnitRequest: (
			state,
			action: PayloadAction<IPayloadDeleteForeignUnitRequest>
		) => {
			state.fetching = true;
		},
		deleteSystemUnitSuccess: (state) => {
			state.fetching = false;
		},
		deleteSystemUnitFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE COMPANY_MANAGEMENT END ---- */

		/**---- HISTORY UPDATE UNIT START ---- */
		getSystemHistoryUpdateSystemUnitListRequest: (
			state,
			action: PayloadAction<TPayloadGetHistoryUpdateForeignUnitListRequest>
		) => {
			const { field, page, pageSize } = action.payload;
			state.fetching = true;
			state.historyParams = {
				page,
				field,
				pageSize,
				total: 0,
			};
		},
		getSystemHistoryUpdateSystemUnitListSuccess: (
			state,
			action: PayloadAction<TPayloadGetForeignSystemHistoryUnitListSuccess>
		) => {
			const { total, historyUpdateUnitList } = action.payload;
			state.fetching = false;
			state.historyUpdateUnitList = historyUpdateUnitList;
			state.historyParams.total = total;
		},
		getSystemHistoryUpdateSystemUnitListFailure: (state) => {
			state.fetching = false;
		},

		/**---- UPDATE COMPANY_MANAGEMENT DETAIL START ---- */
		updateSystemUnitDetailSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<TForeignSystemUnitItem>>
		) => {
			const { data } = action.payload;
			const unitDetail = { ...state.unitDetail };
			state.unitDetail = formatForeignSystemUnitDetail(
				mergeItemUpdated<IForeignUnitDetailStore, TForeignSystemUnitItem>(
					unitDetail,
					data
				)
			);
		},
		/**---- UPDATE COMPANY_MANAGEMENT DETAIL END ---- */
		updateSystemUnitSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<TForeignSystemUnitItem>>
		) => {
			const { data } = action.payload;
			const unitList = [...state.unitList];
			state.unitList = formatForeignSystemUnitList(
				mergeListItemUpdated<IForeignUnitDetailStore, TForeignSystemUnitItem>(
					unitList,
					data
				)
			);
		},

		/**---- GET CONTRACTED KOREAN BUSINESS LIST START ---- */
		getContractedKoreanBusinessListRequest: (
			state,
			action: PayloadAction<IPayloadGetContractedKoreanBusinessListRequest>
		) => {
			const { page, pageSize } = action.payload;
			state.contractedKoreanBusiness.fetching = true;
			state.contractedKoreanBusiness.params = { page, pageSize };
		},
		getContractedKoreanBusinessListSuccess: (
			state,
			action: PayloadAction<IPayloadGetContractedKoreanBusinessListSuccess>
		) => {
			const { content, total } = action.payload;
			state.contractedKoreanBusiness.data = content;
			state.contractedKoreanBusiness.fetching = false;
			state.contractedKoreanBusiness.total = total;
		},
		getContractedKoreanBusinessListFailure: (state) => {
			state.contractedKoreanBusiness.fetching = false;
		},
		/**---- GET CONTRACTED KOREAN BUSINESS LIST END ---- */
	},
});

export const ForeignSystemUnitSelectors = {
	selectFetching: (state: RootState) => state.foreignSystemUnit.fetching,
	selectParams: (state: RootState) => state.foreignSystemUnit.params,
	selectTotal: (state: RootState) => state.foreignSystemUnit.total,
	selectUnitList: (state: RootState) => state.foreignSystemUnit.unitList,
	selectUnitDetail: (state: RootState) => state.foreignSystemUnit.unitDetail,
	selectHistoryUpdateUnitList: (state: RootState) =>
		state.foreignSystemUnit.historyUpdateUnitList,
	selectHistoryParams: (state: RootState) =>
		state.foreignSystemUnit.historyParams,
	selectContractedKoreanBusinessFetching: (state: RootState) =>
		state.foreignSystemUnit.contractedKoreanBusiness.fetching,
	selectContractedKoreanBusiness: (state: RootState) =>
		state.foreignSystemUnit.contractedKoreanBusiness.data,
	selectContractedKoreanBusinessTotal: (state: RootState) =>
		state.foreignSystemUnit.contractedKoreanBusiness.total,
	selectContractedKoreanBusinessParams: (state: RootState) =>
		state.foreignSystemUnit.contractedKoreanBusiness.params,
};

export const ForeignSystemUnitActions = ForeignSystemUnitSlice.actions;
export const ForeignSystemUnitReducer = ForeignSystemUnitSlice.reducer;
